import { useAuth0 } from '@auth0/auth0-react'
import { NestAPI, sessionContext, useGetBusiness, useGetBusinessID } from '@marketing-milk/frontend'
import Avatar from '@material-ui/core/Avatar'
import Badge from '@material-ui/core/Badge'
import Button from '@material-ui/core/Button'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import Drawer from '@material-ui/core/Drawer'
import Fab from '@material-ui/core/Fab'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'
import CommentIcon from '@material-ui/icons/Comment'
import SendIcon from '@material-ui/icons/Send'
import { CreativeStatus } from 'components/creative-status/CreativeStatus'
import { FormPageContent, FormPagePreview } from 'components/layout/PreviewForm'
import { ContentEditableInput } from 'components/mentions/ContentEditableInput'
import { MentionSuggestions } from 'components/mentions/MentionSuggestions'
import { formatDistance, fromUnixTime, format as dateFormat } from 'date-fns'
import { useMentions } from 'hooks/useMentions'
import { useSlackUsers } from 'hooks/useSlackUsers'
import { adCreativeFormatWire } from 'pages/business/creatives/creative-wizard/util'
import { useContext, useEffect, useRef, useState } from 'react'
import { AdPreview } from './ad-preview/AdPreview'
import { BasicAdPreview } from './ad-preview/AdPreview.type'
import { creativeContext } from './context/CreativeContext'
import { CreativeContextProvider } from './context/CreativeContextProvider'
import './creative-wizard.scss'
import { CreativeWizardProps } from './CreativeWizard.types'
import { getCreativeFormat } from './format-views/get-format'
import { useGetCreativeMessages } from '../useGetCreativeMessages'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiFormLabel-root': {
        fontWeight: 'bold',
      },
    },
    paperContainer: {
      backgroundImage:
        "url('https://www.marketingmilk.com/wp-content/uploads/2021/07/empty-table-bg.jpg')",
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
    },
    commentsBadge: {
      marginLeft: theme.spacing(2),
      '& .MuiBadge-badge': {
        fontSize: '0.9rem',
        height: 20,
        minWidth: 20,
        padding: '0 6px',
      },
    },
    drawerPaper: {
      zIndex: 1300,
    },
    commentDrawer: {
      width: 450,
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    commentItem: {
      padding: theme.spacing(3),
      backgroundColor: theme.palette.grey[100],
      borderRadius: theme.spacing(1.5),
      display: 'flex',
      gap: theme.spacing(2.5),
      maxWidth: '90%',
      border: '2px solid transparent',
      transition: 'border-color 0.3s ease-in-out',
    },
    commentItemCurrentUser: {
      backgroundColor: theme.palette.primary.main,
      flexDirection: 'row-reverse',
      '& $commentAuthor': {
        color: theme.palette.primary.contrastText,
        textAlign: 'right',
      },
      '& $commentTime': {
        color: theme.palette.primary.contrastText,
        opacity: 0.8,
        textAlign: 'right',
      },
      '& $commentText': {
        color: theme.palette.primary.contrastText,
      },
    },
    commentAvatar: {
      width: theme.spacing(5),
      height: theme.spacing(5),
      fontSize: '0.95rem',
    },
    commentContent: {
      flex: 1,
    },
    commentAuthor: {
      fontWeight: 'bold',
      fontSize: '0.9rem',
      marginBottom: theme.spacing(0.75),
    },
    commentTime: {
      fontSize: '0.85rem',
      color: theme.palette.grey[600],
      marginBottom: theme.spacing(1.25),
      cursor: 'help',
    },
    commentText: {
      fontSize: '1rem',
      lineHeight: 1.6,
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
    },
    commentsList: {
      flex: 1,
      overflowY: 'auto',
      marginTop: theme.spacing(3),
    },
    commentWrapper: {
      display: 'flex',
      marginBottom: theme.spacing(3),
      '&.current-user': {
        justifyContent: 'flex-end',
      },
      '&.highlighted $commentItem': {
        animation: '$highlight 2s ease-in-out',
      },
    },
    '@keyframes highlight': {
      '0%': {
        borderColor: 'transparent',
      },
      '10%': {
        borderColor: theme.palette.info.main,
      },
      '90%': {
        borderColor: theme.palette.info.main,
      },
      '100%': {
        borderColor: 'transparent',
      },
    },
    commentInputContainer: {
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.grey[300]}`,
      display: 'flex',
      gap: theme.spacing(1),
      position: 'relative',
    },
  })
)

export const creativePreviews: Array<{ format: BasicAdPreview; icon: string }> = [
  { format: 'facebook', icon: 'fab fa-facebook-f' },
  { format: 'messenger', icon: 'fab fa-facebook-messenger' },
  { format: 'marketplace', icon: 'fas fa-store' },
  { format: 'instagram', icon: 'fab fa-instagram' },
]

export function CreativeWizardComponent() {
  const classes = useStyles()
  const business = useGetBusiness()
  const businessID = String(business.id)
  const { user } = useContext(sessionContext)

  const { creative, format, preview, setPreview } = useContext(creativeContext)
  const { comments, refetch: refetchComments } = useGetCreativeMessages({
    businessID,
    adCreativeID: creative.id,
  })

  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [cursorPosition, setCursorPosition] = useState(0)
  const [controlledCursorPosition, setControlledCursorPosition] = useState<number | undefined>(
    undefined
  )
  const [highlightedCommentId, setHighlightedCommentId] = useState<number | null>(null)
  const hasScrolledToComment = useRef(false)
  const hasScrolledToSpecificComment = useRef(false)

  // Ref for comments list container to enable auto-scroll
  const commentsListRef = useRef<HTMLDivElement>(null)
  const commentRefs = useRef<Record<number, HTMLDivElement | null>>({})

  // Fetch Slack users
  const { users: slackUsers } = useSlackUsers()

  // Handle mentions
  const {
    isShowingSuggestions,
    filteredUsers,
    selectedIndex,
    selectUser,
    handleKeyDown,
    taggedUsers,
    clearTaggedUsers,
  } = useMentions(commentText, cursorPosition, slackUsers)

  // Sort comments by created date (oldest first)
  const sortedComments = [...comments].sort((a, b) => a.createdAt - b.createdAt)

  // Auto-open drawer if commentId is in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const commentId = urlParams.get('commentId')
    if (commentId && !commentDrawerOpen) {
      setCommentDrawerOpen(true)
    }
  }, [commentDrawerOpen])

  // Auto-scroll to bottom when comments change or drawer opens
  // OR scroll to specific comment if commentId is in URL
  useEffect(() => {
    if (commentDrawerOpen && !hasScrolledToComment.current) {
      // Check for commentId in URL
      const urlParams = new URLSearchParams(window.location.search)
      const commentId = urlParams.get('commentId')

      // Wait for drawer animation and DOM rendering to complete
      const timer = setTimeout(() => {
        if (commentId && commentRefs.current[Number(commentId)]) {
          // Scroll to the specific comment mentioned in Slack
          commentRefs.current[Number(commentId)]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
          // Highlight the comment
          setHighlightedCommentId(Number(commentId))
          // Remove highlight after animation
          setTimeout(() => setHighlightedCommentId(null), 2000)
          // Mark that we've scrolled to a specific comment - don't auto-scroll anymore
          hasScrolledToComment.current = true
          hasScrolledToSpecificComment.current = true
        } else if (sortedComments.length > 0 && !commentId) {
          // Default behavior: scroll to the last comment (only if no commentId in URL)
          const lastComment = sortedComments[sortedComments.length - 1]
          commentRefs.current[lastComment.id]?.scrollIntoView({ behavior: 'auto', block: 'end' })
          hasScrolledToComment.current = true
        }
      }, 400)

      return () => clearTimeout(timer)
    }
  }, [sortedComments, commentDrawerOpen])

  // Auto-scroll to new comments when they arrive (but not if we scrolled to a specific comment from URL)
  useEffect(() => {
    if (
      commentDrawerOpen &&
      hasScrolledToComment.current &&
      !hasScrolledToSpecificComment.current &&
      sortedComments.length > 0
    ) {
      const lastComment = sortedComments[sortedComments.length - 1]
      // Scroll to bottom when new comments arrive
      setTimeout(() => {
        commentRefs.current[lastComment.id]?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }, 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedComments.length, commentDrawerOpen])

  // Helper function to format relative time
  const getRelativeTime = (timestamp: number) => {
    const date = fromUnixTime(timestamp)
    return formatDistance(date, new Date(), { addSuffix: true })
  }

  // Helper function to format full timestamp
  const getFullTimestamp = (timestamp: number) => {
    const date = fromUnixTime(timestamp)
    return dateFormat(date, 'PPpp') // e.g., "Apr 29, 2021, 9:30:00 AM"
  }

  // Helper function to check if comment is from current user
  const isCurrentUser = (commentUserEmail: string) => {
    return user?.email === commentUserEmail
  }

  // Handle sending comment
  const handleSendComment = async () => {
    if (!commentText.trim()) return

    try {
      // Send comment and get the response with the new comment ID
      const response = await NestAPI.post<{ id: number }>(
        `businesses/${businessID}/ad-creatives/${creative.id}/comments`,
        {
          message: commentText,
        }
      )

      const newCommentId = response.data.id

      // Prepare payload with message, tagged users, and page URL with comment ID
      const baseUrl = window.location.href.split('?')[0]
      const slackPayload = {
        message: commentText,
        header: `${user?.firstName + ' ' + user?.lastName} mentioned you on this creative: "${
          creative.name
        }" for business: "${business.businessName}"`,
        taggedUsers: taggedUsers.map(user => ({
          id: user.id,
          displayName: user.displayName,
        })),
        pageUrl: `${baseUrl}?commentId=${newCommentId}`,
      }

      // Send Slack notification if users are tagged
      if (taggedUsers.length) {
        await NestAPI.post('/slack/send', slackPayload)
      }

      // User is actively engaging, allow auto-scroll to their new message
      hasScrolledToSpecificComment.current = false

      // Clear input and refetch comments
      setCommentText('')
      setCursorPosition(0)
      clearTaggedUsers()
      refetchComments()
    } catch (error) {
      console.error('Error sending comment:', error)
    }
  }

  // Handle Enter key to send message
  const handleKeyDownWithEnter = (e: React.KeyboardEvent) => {
    // Allow Shift+Enter to create new line without any interference
    if (e.key === 'Enter' && e.shiftKey) {
      // Don't prevent default, don't call handleKeyDown - just let it happen naturally
      return
    }

    // Handle Enter to send (without Shift, when not showing suggestions)
    if (e.key === 'Enter' && !e.shiftKey && !isShowingSuggestions) {
      e.preventDefault()
      handleSendComment()
      return
    }

    // Handle other keys (for mentions, navigation in suggestions, etc.)
    const result = handleKeyDown(e)
    if (
      result &&
      typeof result === 'object' &&
      result.newValue &&
      result.newCursorPosition !== undefined
    ) {
      setCommentText(result.newValue)
      setControlledCursorPosition(result.newCursorPosition)
    }
  }

  // Reset controlled cursor position after it's been used
  useEffect(() => {
    if (controlledCursorPosition !== undefined) {
      const timer = setTimeout(() => {
        setControlledCursorPosition(undefined)
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [controlledCursorPosition])

  return (
    <>
      <div className="sm:space-x-0 sm:flex flex-grow sm:p-4 space-x-4">
        <FormPageContent>
          <div className="p-3">
            <p className="my-2 text-xl font-semibold">General Settings</p>
            {getCreativeFormat(format, creative)}
          </div>
        </FormPageContent>

        <FormPagePreview>
          <div className="flex justify-between">
            <div className="flex items-center">
              <Typography variant="h5" className="d-inline-block">
                {adCreativeFormatWire(format)}
              </Typography>
              {creative && (
                <div className="d-inline-block ml-3">
                  <CreativeStatus creative={creative} />
                </div>
              )}
            </div>
            <div className="flex items-center">
              <ButtonGroup variant="contained" aria-label="creative preview format options">
                {creativePreviews.map((x, i) => (
                  <Button
                    color={preview === x.format ? 'secondary' : 'default'}
                    size="large"
                    onClick={() => setPreview(x.format)}
                  >
                    <i className={`${x.icon}`} />
                  </Button>
                ))}
              </ButtonGroup>
            </div>
            <div className="flex items-center">
              <Badge
                badgeContent={comments.length}
                color="secondary"
                overlap="circle"
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                className={classes.commentsBadge}
              >
                <Fab
                  variant="extended"
                  color="primary"
                  aria-label="comments"
                  onClick={() => setCommentDrawerOpen(true)}
                >
                  <CommentIcon className="mr-1" />
                  Comments
                </Fab>
              </Badge>
            </div>
          </div>

          <div className="relative h-full">
            <AdPreview />
          </div>
        </FormPagePreview>
      </div>

      <Drawer
        anchor="right"
        open={commentDrawerOpen}
        variant="persistent"
        hideBackdrop={true}
        classes={{
          paper: classes.drawerPaper,
        }}
        ModalProps={{
          keepMounted: true,
          disableEnforceFocus: true,
          disableAutoFocus: true,
        }}
        onClose={() => {
          setCommentDrawerOpen(false)
          // Clear commentId from URL when closing drawer
          const urlParams = new URLSearchParams(window.location.search)
          if (urlParams.has('commentId')) {
            const newUrl = window.location.pathname
            window.history.replaceState({}, '', newUrl)
          }
          // Reset the scroll flags so it works properly on next open
          hasScrolledToComment.current = false
          hasScrolledToSpecificComment.current = false
        }}
      >
        <div className={classes.commentDrawer}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Typography variant="h5" gutterBottom>
                Comments
              </Typography>
            </div>
            <IconButton
              onClick={() => {
                setCommentDrawerOpen(false)
                // Clear commentId from URL when closing drawer
                const urlParams = new URLSearchParams(window.location.search)
                if (urlParams.has('commentId')) {
                  const newUrl = window.location.pathname
                  window.history.replaceState({}, '', newUrl)
                }
                // Reset the scroll flags so it works properly on next open
                hasScrolledToComment.current = false
                hasScrolledToSpecificComment.current = false
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <Typography variant="body2" color="textSecondary">
            {comments.length} comment{comments.length !== 1 ? 's' : ''}
          </Typography>

          <div ref={commentsListRef} className={classes.commentsList}>
            {sortedComments.map(comment => {
              const isOwnComment = isCurrentUser(comment.user.email)
              const isHighlighted = highlightedCommentId === comment.id
              return (
                <div
                  key={comment.id}
                  ref={el => {
                    commentRefs.current[comment.id] = el
                  }}
                  className={`${classes.commentWrapper} ${isOwnComment ? 'current-user' : ''} ${
                    isHighlighted ? 'highlighted' : ''
                  }`}
                >
                  <div
                    className={`${classes.commentItem} ${
                      isOwnComment ? classes.commentItemCurrentUser : ''
                    }`}
                  >
                    <Avatar
                      className={classes.commentAvatar}
                      src={comment.user.profilePicture}
                      alt={`${comment.user.firstName} ${comment.user.lastName}`}
                    >
                      {!comment.user.profilePicture &&
                        `${comment.user.firstName[0]}${comment.user.lastName[0]}`}
                    </Avatar>
                    <div className={classes.commentContent}>
                      <div className={classes.commentAuthor}>
                        {comment.user.firstName} {comment.user.lastName}
                        {isOwnComment && ' (You)'}
                      </div>
                      <Tooltip title={getFullTimestamp(comment.createdAt)} placement="top">
                        <div className={classes.commentTime}>
                          {getRelativeTime(comment.createdAt)}
                        </div>
                      </Tooltip>
                      <Typography variant="body2" className={classes.commentText}>
                        {comment.message}
                      </Typography>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <div className={classes.commentInputContainer}>
            {isShowingSuggestions && (
              <MentionSuggestions
                users={filteredUsers}
                selectedIndex={selectedIndex}
                onSelect={user => {
                  const result = selectUser(user)
                  if (result) {
                    setCommentText(result.newValue)
                    setControlledCursorPosition(result.newCursorPosition)
                  }
                }}
              />
            )}
            <ContentEditableInput
              value={commentText}
              onChange={setCommentText}
              onCursorPositionChange={setCursorPosition}
              cursorPosition={controlledCursorPosition}
              onKeyDown={handleKeyDownWithEnter}
              placeholder="Add a comment... (@ to mention, Enter to send, Shift+Enter for new line)"
              taggedUsers={taggedUsers}
            />
            <Button
              variant="contained"
              color="primary"
              endIcon={<SendIcon />}
              onClick={handleSendComment}
              disabled={!commentText.trim()}
            >
              Send
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  )
}

export function CreativeWizard({ defaultCreative }: CreativeWizardProps) {
  return (
    <CreativeContextProvider defaultCreative={defaultCreative}>
      <CreativeWizardComponent />
    </CreativeContextProvider>
  )
}
